import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import {
  AspectRatio,
  Box,
  Button,
  Center,
  Flex,
  Heading,
  HStack,
  Spinner,
  Text,
  useMediaQuery,
} from '@chakra-ui/react';
import InputNumber from 'components/inputs/InputNumber';
import UtilPriceFormatter from 'components/utils/UtilPriceFormatter';
import useCart from 'hooks/useCart';
import useCartItem from 'hooks/useCartItem';
import useCatchError from 'hooks/useCatchError';
import useProduct from 'hooks/useProduct';
import useProductItem from 'hooks/useProductItem';
import useUser from 'hooks/useUser';
import { ICartItem } from 'interfaces/cart.interface';
import { debounce } from 'lodash';
import Image from 'next/image';
import NextLink from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { CartRepository } from 'respository/cart.repository';

type CartItemProps = {
  item: ICartItem;
};

function CartItem({ item }: CartItemProps) {
  // use user
  const { user } = useUser();

  // use media query
  const [isTablet] = useMediaQuery('(min-width: 768px)');
  const [isDesktop] = useMediaQuery('(min-width: 992px)');

  // use catch error
  const catchError = useCatchError();

  // use cart
  const { cartItemDelete, cartItemQuantityUpdate, cartItemSelect } = useCart();

  // use cart item
  const { sku, isRemoveCartItem, isAvailableForPurchase, setIsRemoveCartItem } =
    useCartItem({ item });

  // use product item
  const {
    name: productName,
    featuredImage,
    variationNameWithAttributes,
  } = useProductItem({
    product: item.product,
    variation: item.variation,
  });

  // init quantity
  const [quantity, setQuantity] = useState<number>(item.quantity);

  // init has buy 1 get 1
  const [hasBuy1Get1, setHasBuy1Get1] = useState<boolean>(false);

  // init cart remove loading
  const [isCartRemoveLoading, setIsCartRemoveLoading] =
    useState<boolean>(false);

  // init cart select loading state
  const [isCartSelectLoading, setIsCartSelectLoading] =
    useState<boolean>(false);

  useEffect(() => {
    setQuantity(item.quantity);

    setHasBuy1Get1(item.buy1Get1Id && item.buy1Get1Id > 0 ? true : false);
  }, [item]);

  const cartItemQuantityUpdateApi = async (key: string, quantity: number) => {
    try {
      const itemUpdated = await CartRepository.updateQuantity({
        key,
        quantity,
      });

      console.log('itemUpdated', itemUpdated);

      if (itemUpdated.buy1Get1Id && itemUpdated.buy1Get1Id > 0) {
        setHasBuy1Get1(true);
      }
    } catch (error) {
      catchError(error);
    }
  };

  const debouncedUpdateQuantity = useRef(
    debounce(cartItemQuantityUpdateApi, 1000)
  ).current;

  const handleCartRemoveItem = async () => {
    try {
      setIsRemoveCartItem(true);
      setIsCartRemoveLoading(true);

      if (user && user.isAuthenticated) {
        await CartRepository.removeItem(item.key);
      }

      cartItemDelete(item.key);
      setIsRemoveCartItem(false);
    } catch (error) {
      catchError(error);
    } finally {
      setIsCartRemoveLoading(false);
    }
  };

  const handleCartItemSelect = async () => {
    try {
      if (user && user.isAuthenticated) {
        setIsCartSelectLoading(true);
        await CartRepository.itemSelectUpdate(item.key, !item.selected);
      }

      cartItemSelect(item.key, !item.selected);
    } catch (error) {
      catchError(error);
    } finally {
      setIsCartSelectLoading(false);
    }
  };

  // log cart item
  console.log('item', item);

  return (
    <Box
      bg="#EADED0"
      px={{
        base: 2,
        md: 4,
      }}
      py={{
        base: 4,
        md: 4,
      }}
      opacity={!isAvailableForPurchase ? 0.7 : 1}
    >
      <HStack
        spacing={{
          base: 2,
          md: 4,
        }}
        alignItems="initial"
      >
        <Box flex="0 0 auto">
          <Button
            boxSize={{
              base: '25px',
              lg: '30px',
            }}
            minW="auto"
            padding="0"
            bg="white"
            variant="unstyled"
            border="1px solid white"
            justifyContent="center"
            alignItems="center"
            _hover={{
              borderColor: 'primary.500',
            }}
            onClick={handleCartItemSelect}
            isDisabled={isCartSelectLoading || !isAvailableForPurchase}
          >
            {isCartSelectLoading ? (
              <Center boxSize="100%">
                <Spinner size="md"></Spinner>
              </Center>
            ) : (
              <>
                {item.selected && (
                  <Box textAlign="center">
                    <Image
                      src="/image-icon/check-active.png"
                      width={20}
                      height={20}
                      alt="checked"
                      style={{
                        margin: 'auto',
                        width: isDesktop ? '20px' : isTablet ? '20px' : '16px',
                      }}
                    ></Image>
                  </Box>
                )}
              </>
            )}
          </Button>
        </Box>
        <Box flex="0 0 auto">
          <Box
            boxSize={{
              base: '60px',
              md: '80px',
              lg: '80px',
            }}
          >
            <AspectRatio ratio={1}>
              <NextLink
                href={`
                /products/${item.product.permalink}
              `}
              >
                <Image
                  src={featuredImage}
                  alt="featuredImage"
                  width={80}
                  height={80}
                ></Image>
              </NextLink>
            </AspectRatio>
          </Box>
        </Box>
        <Box flex="1">
          <Heading
            fontSize={{
              base: 'sm',
              md: 'md',
              lg: '2xl',
            }}
            fontWeight={400}
            textTransform="uppercase"
            mb={1}
          >
            <NextLink
              href={`
                /products/${item.product.permalink}
              `}
            >
              {productName}
            </NextLink>
          </Heading>
          {variationNameWithAttributes.length > 0 && (
            <HStack mb={1}>
              {variationNameWithAttributes.map((attributeName, index) => (
                <Text
                  key={index}
                  fontSize={{
                    base: 'xs',
                    md: 'sm',
                  }}
                  whiteSpace="nowrap"
                >
                  {attributeName}
                </Text>
              ))}
            </HStack>
          )}
          <HStack>
            <Text
              fontSize={{
                base: 'xs',
                md: 'sm',
              }}
              whiteSpace="nowrap"
            >
              Seller SKU : {sku}
            </Text>
          </HStack>
        </Box>
        <Flex
          flex={{
            base: '1 1 100%',
            md: '0 0 70px',
          }}
          textAlign="right"
          flexDirection="column"
          justifyContent="space-between"
          alignItems="flex-end"
        >
          {isRemoveCartItem ? (
            <HStack justifyContent="flex-end" width="100%">
              <Button
                variant="unstyled"
                p={0}
                minW="0"
                height="auto"
                color="primary.500"
                onClick={handleCartRemoveItem}
                isLoading={isCartRemoveLoading}
              >
                <CheckOutlined></CheckOutlined>
              </Button>
              <Button
                variant="unstyled"
                p={0}
                minW="0"
                height="auto"
                color="gray.500"
                onClick={() => {
                  setIsRemoveCartItem(false);
                }}
              >
                <CloseOutlined></CloseOutlined>
              </Button>
            </HStack>
          ) : (
            <Button
              variant="unstyled"
              p={0}
              minW="0"
              height="auto"
              onClick={() => {
                setIsRemoveCartItem(true);
              }}
            >
              <Image
                src="/image-icon/trash.png"
                alt="trash"
                width={13}
                height={18}
                quality="100"
              ></Image>
            </Button>
          )}
          <Box flex="0 0 auto">
            {item.salePrice > 0 && item.salePrice !== item.regularPrice && (
              <Text fontSize="sm" color="gray.500" as="s" whiteSpace="nowrap">
                <UtilPriceFormatter
                  price={item.regularPrice}
                ></UtilPriceFormatter>
              </Text>
            )}
            <Text fontWeight="500" fontSize="17px" whiteSpace="nowrap">
              <UtilPriceFormatter
                price={item.salePrice ? item.salePrice : item.regularPrice}
              ></UtilPriceFormatter>
            </Text>
          </Box>
        </Flex>
      </HStack>
      <Box
        textAlign="right"
        borderTop="1px solid #666"
        mt={3}
        pt={2}
        justifyContent="flex-end"
      >
        {/* <Box fontSize="sm">
          {item.weight} ก. x {quantity} = {item.weightTotal} ก.
        </Box> */}
        <HStack justifyContent="flex-end">
          {isAvailableForPurchase ? (
            <Box flex="0 0 70px" alignSelf="flex-end">
              <HStack flex="0 0 70px" bg="white">
                <InputNumber
                  size="xs"
                  step={1}
                  min={1}
                  max={item.maxQuantity}
                  value={quantity ? quantity : 1}
                  isDisabled={isAvailableForPurchase ? false : true}
                  onChange={(valueAsString: string, valueAsNumber: number) => {
                    if (!isAvailableForPurchase) {
                      return;
                    }

                    let _quantity = valueAsNumber;

                    setQuantity(_quantity);

                    cartItemQuantityUpdate(item.key, _quantity);
                  }}
                  onBlur={() => {
                    if (user && user.isAuthenticated) {
                      debouncedUpdateQuantity(item.key, quantity);
                    }
                  }}
                ></InputNumber>
              </HStack>
            </Box>
          ) : (
            <Box flex="0 0 auto" alignSelf="flex-end">
              <Text color="red.500">ไม่พร้อมให้บริการ</Text>
            </Box>
          )}
          {item.subTotal && (
            <Box textAlign="right" minW="100px">
              <Text
                fontSize="2xl"
                color="primary.500"
                fontWeight={400}
                lineHeight={1}
              >
                <UtilPriceFormatter price={item.subTotal}></UtilPriceFormatter>
              </Text>
            </Box>
          )}
        </HStack>
        {item.buyXGetX ? (
          <Box mt={3} mb={-2}>
            <Text fontSize="sm" color="primary.500" fontWeight={500}>
              🎁 แถมฟรี: {item.buyXGetX.getX * item.quantity} ชิ้น
            </Text>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

export default CartItem;
